import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  Stack,
  Grid,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  ArrowBackIosNew,
  Settings,
  GridOn,
  VideoLibrary,
  BookmarkBorder,
  Edit,
  Bookmark,
  Repeat,
  DeleteOutline,
  Campaign,
  Favorite,
  FavoriteBorder,
  ChatBubbleOutline,
  Send,
  IosShare,
  MoreVert,
  Close,
  AddCircleOutline,
  LinkOutlined,
  WorkOutline,
  StarBorderOutlined,
  Verified,
} from "@mui/icons-material";
import { updateProfile } from "firebase/auth";
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  increment,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "./firebase";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";
import Comentarios from "./Comentarios";
import PostTextBlock from "./PostTextBlock";

const PROFILE_TEXT = {
  es: {
    myProfile: "Mi Perfil",
    posts: "Posts",
    followers: "Seguidores",
    following: "Seguidos",
    editProfile: "Editar Perfil",
    noPosts: "Aún no tienes publicaciones.",
    noVideoPosts: "Aún no tienes videos.",
    noSavedPosts: "No tienes publicaciones guardadas.",
    loadProfileError: "No se pudo cargar toda la información del perfil.",
    shareFallbackText: "Mira esta publicación en Gexvi",
    textCopied: "Texto copiado al portapapeles.",
    linkCopied: "Copiado al portapapeles.",
    followToggleError: "No se pudo actualizar el seguimiento.",
    userFallback: "Usuario",
    postReposted: "Publicación republicada.",
    repostError: "No se pudo republicar.",
    sharedPostMessage: "Te compartió una publicación",
    postSentSuccess: "Publicación enviada correctamente.",
    postSendError: "No se pudo enviar la publicación.",
    postEdited: "Publicación editada.",
    postEditError: "No se pudo editar la publicación.",
    postDeleted: "Publicación eliminada.",
    postDeleteError: "No se pudo eliminar.",
    invalidImageFile: "Selecciona un archivo de imagen válido.",
    nameRequired: "El nombre no puede estar vacío.",
    profileUpdated: "Perfil actualizado correctamente.",
    profileUpdateError: "No se pudo guardar el perfil. Intenta nuevamente.",
    usernameFallback: "usuario",
    noTextFallback: "Sin texto",
    editProfileTitle: "Editar perfil",
    uploadImage: "Subir imagen",
    noFileSelected: "Sin archivo seleccionado",
    nameLabel: "Nombre",
    bioLabel: "Bio",
    websitesLabel: "Sitios web",
    addWebsite: "Agregar sitio web",
    employmentLabel: "Empleo / Empresa",
    positionLabel: "Puesto / Cargo",
    interestsLabel: "Intereses",
    interestsPlaceholder: "Tecnología, diseño, música...",
    cancel: "Cancelar",
    save: "Guardar",
    peopleWhoFollowYou: "Personas que te siguen",
    peopleYouFollow: "Personas que sigues",
    noUsersToShow: "No hay usuarios para mostrar.",
    followed: "Seguido",
    follow: "Seguir",
    pending: "Pendiente",
    userUnfollowed: "Ya no sigues a este usuario.",
    requestCancelled: "Solicitud cancelada.",
    requestSent: "Solicitud enviada.",
    suggestions: "Sugerencias",
    suggestionsDesc: "Ordenadas por cantidad de seguidos en común",
    noSuggestions: "No hay sugerencias disponibles por ahora.",
    mutualFollowers: "seguidos en común",
    sendToContacts: "Enviar a contactos",
    noContacts: "No sigues a ningún contacto todavía.",
    send: "Enviar",
    close: "Cerrar",
    menuEdit: "Editar",
    menuDelete: "Eliminar",
    menuPromote: "Promocionar",
    editPostTitle: "Editar publicación",
    editPostPlaceholder: "¿Qué quieres publicar?",
    deletePostTitle: "¿Eliminar publicación?",
    deletePostWarning: "Esta acción no se puede deshacer.",
  },
  en: {
    myProfile: "My Profile",
    posts: "Posts",
    followers: "Followers",
    following: "Following",
    editProfile: "Edit Profile",
    noPosts: "You do not have posts yet.",
    noVideoPosts: "You do not have videos yet.",
    noSavedPosts: "You do not have saved posts.",
    loadProfileError: "Could not load all of your profile information.",
    shareFallbackText: "Check out this post on Gexvi",
    textCopied: "Text copied to clipboard.",
    linkCopied: "Copied to clipboard.",
    followToggleError: "Could not update the follow status.",
    userFallback: "User",
    postReposted: "Post reposted.",
    repostError: "Could not repost.",
    sharedPostMessage: "Shared a post with you",
    postSentSuccess: "Post sent successfully.",
    postSendError: "Could not send the post.",
    postEdited: "Post edited.",
    postEditError: "Could not edit the post.",
    postDeleted: "Post deleted.",
    postDeleteError: "Could not delete.",
    invalidImageFile: "Select a valid image file.",
    nameRequired: "Name cannot be empty.",
    profileUpdated: "Profile updated successfully.",
    profileUpdateError: "Could not save the profile. Please try again.",
    usernameFallback: "user",
    noTextFallback: "No text",
    editProfileTitle: "Edit profile",
    uploadImage: "Upload image",
    noFileSelected: "No file selected",
    nameLabel: "Name",
    bioLabel: "Bio",
    websitesLabel: "Websites",
    addWebsite: "Add website",
    employmentLabel: "Employment / Company",
    positionLabel: "Position / Role",
    interestsLabel: "Interests",
    interestsPlaceholder: "Technology, design, music...",
    cancel: "Cancel",
    save: "Save",
    peopleWhoFollowYou: "People who follow you",
    peopleYouFollow: "People you follow",
    noUsersToShow: "No users to show.",
    followed: "Following",
    follow: "Follow",
    pending: "Pending",
    userUnfollowed: "You no longer follow this user.",
    requestCancelled: "Request cancelled.",
    requestSent: "Request sent.",
    suggestions: "Suggestions",
    suggestionsDesc: "Sorted by mutual following count",
    noSuggestions: "No suggestions available right now.",
    mutualFollowers: "mutual following",
    sendToContacts: "Send to contacts",
    noContacts: "You aren't following any contacts yet.",
    send: "Send",
    close: "Close",
    menuEdit: "Edit",
    menuDelete: "Delete",
    menuPromote: "Promote",
    editPostTitle: "Edit post",
    editPostPlaceholder: "What do you want to post?",
    deletePostTitle: "Delete post?",
    deletePostWarning: "This action cannot be undone.",
  },
};

const Perfil = ({ onBack, onOpenProfile, onOpenSettings, onNavigate, language = "es", themeMode = "oscuro" }) => {
  const navigate = useNavigate();
  const isLightTheme = themeMode === "claro";
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const dialogBg = isLightTheme ? "#ffffff" : "#0b1016";
  const dialogGradient = isLightTheme
    ? "radial-gradient(circle at top, rgba(0,150,180,0.06) 0%, rgba(255,255,255,1) 55%)"
    : "radial-gradient(circle at top, rgba(0,212,255,0.16) 0%, rgba(11,16,22,1) 48%)";
  const t = useTranslatedDictionary(PROFILE_TEXT, language);
  const defaultBio = "Explorando la nueva red social. 🚀 | Tech & News.";
  const user = auth.currentUser;
  const [openEdit, setOpenEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, type: "success", text: "" });
  const fileInputRef = useRef(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState("");
  const [activeTab, setActiveTab] = useState("imagenes");
  const [savedPosts, setSavedPosts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [postProfilesByUid, setPostProfilesByUid] = useState({});
  const [resolvedPhotosByUid, setResolvedPhotosByUid] = useState({});
  const [myFollowingIds, setMyFollowingIds] = useState([]);
  const [pendingFollowRequestIds, setPendingFollowRequestIds] = useState([]);
  const [openConnectionsDialog, setOpenConnectionsDialog] = useState(false);
  const [connectionsTab, setConnectionsTab] = useState("followers");
  const [followLoadingUid, setFollowLoadingUid] = useState("");
  const [selectedPost, setSelectedPost] = useState(null);
  const [openPostMenu, setOpenPostMenu] = useState(false);
  const [anchorElActions, setAnchorElActions] = useState(null);
  const [openComentariosPost, setOpenComentariosPost] = useState(false);
  const [openEditPost, setOpenEditPost] = useState(false);
  const [editPostText, setEditPostText] = useState("");
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [savingPost, setSavingPost] = useState(false);
  const [openSendPostDialog, setOpenSendPostDialog] = useState(false);
  const [sendingPostUid, setSendingPostUid] = useState("");
  const [profile, setProfile] = useState({
    displayName: user?.displayName || "",
    username: "",
    photoURL: user?.photoURL || "",
    bio: defaultBio,
    followersCount: 0,
    followingCount: 0,
    verified: false,
    websites: [],
    employment: "",
    position: "",
    interests: "",
  });
  const [form, setForm] = useState({
    displayName: user?.displayName || "",
    photoURL: user?.photoURL || "",
    bio: defaultBio,
    websites: [],
    employment: "",
    position: "",
    interests: "",
  });
  const [userPosts, setUserPosts] = useState([]);

  useEffect(() => {
    const loadProfile = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const baseProfile = {
        displayName: currentUser.displayName || "",
        photoURL: currentUser.photoURL || "",
        bio: defaultBio,
        followersCount: 0,
        followingCount: 0,
        verified: false,
      };

      setProfile(baseProfile);
      setForm(baseProfile);

      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          // websites: soporta string legacy o array
          const rawWebsites = data?.websites ?? data?.website ?? [];
          const websitesArr = Array.isArray(rawWebsites)
            ? rawWebsites.map(String).filter(Boolean)
            : rawWebsites ? [String(rawWebsites)] : [];
          const merged = {
            ...baseProfile,
            displayName: data?.displayName || baseProfile.displayName,
            username: data?.username || "",
            photoURL: data?.photoURL || baseProfile.photoURL,
            bio: data?.bio || defaultBio,
            followersCount: data?.followersCount || 0,
            followingCount: data?.followingCount || 0,
            verified: Boolean(data?.verified),
            websites: websitesArr,
            employment: typeof data?.employment === "string" ? data.employment : "",
            position: typeof data?.position === "string" ? data.position : "",
            interests: typeof data?.interests === "string" ? data.interests : "",
          };
          setMyFollowingIds(Array.isArray(data?.following) ? data.following : []);
          setProfile(merged);
          setForm({
            displayName: merged.displayName,
            photoURL: merged.photoURL,
            bio: merged.bio,
            websites: merged.websites,
            employment: merged.employment,
            position: merged.position,
            interests: merged.interests,
          });
        }
      } catch (error) {
        setToast({
          open: true,
          type: "error",
          text: t("loadProfileError"),
        });
      }
    };

    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    const unsubscribe = onSnapshot(doc(db, "users", currentUser.uid), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() || {};
      setMyFollowingIds(Array.isArray(data.following) ? data.following : []);
      setProfile((prev) => ({
        ...prev,
        followersCount: data.followersCount || 0,
        followingCount: data.followingCount || 0,
        verified: Boolean(data.verified),
      }));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    const q = query(
      collection(db, "follow_requests"),
      where("requesterUid", "==", currentUser.uid),
      where("status", "==", "pending")
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const pending = snap.docs.map((d) => d.data().targetUid).filter(Boolean);
      setPendingFollowRequestIds(pending);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "users"), (snap) => {
      setAllUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "posts"), (snap) => {
      const profileMap = {};
      snap.docs.forEach((d) => {
        const post = d.data() || {};
        if (!post.uid) return;
        const current = profileMap[post.uid] || {};
        profileMap[post.uid] = {
          displayName: current.displayName || post.username || "",
          handle: current.handle || post.handle || "",
          photoURL: current.photoURL || post.userImg || post.photoURL || post.photoUrl || "",
        };
      });
      setPostProfilesByUid(profileMap);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const getRawPhoto = (userItem) => {
      const postProfile = postProfilesByUid[userItem?.uid] || {};
      return (
        userItem?.photoURL ||
        userItem?.photoUrl ||
        userItem?.userImg ||
        userItem?.avatar ||
        postProfile.photoURL ||
        ""
      ).trim();
    };

    const needsStorageResolution = (value) => {
      if (!value) return false;
      const isHttp = /^https?:\/\//i.test(value);
      const isDataOrBlob = /^(data:|blob:|\/)/i.test(value);
      return !isHttp && !isDataOrBlob;
    };

    const resolvePhotos = async () => {
      const candidates = allUsers
        .map((u) => ({ uid: u.uid, raw: getRawPhoto(u) }))
        .filter((item) => item.uid && item.raw && !resolvedPhotosByUid[item.uid]);

      if (!candidates.length) return;

      const entries = {};
      await Promise.all(
        candidates.map(async ({ uid: candidateUid, raw }) => {
          let finalUrl = raw;
          if (needsStorageResolution(raw)) {
            try {
              finalUrl = await getDownloadURL(ref(storage, raw));
            } catch {
              finalUrl = raw;
            }
          }
          entries[candidateUid] = finalUrl;
        })
      );

      if (!cancelled && Object.keys(entries).length > 0) {
        setResolvedPhotosByUid((prev) => ({ ...prev, ...entries }));
      }
    };

    resolvePhotos();
    return () => {
      cancelled = true;
    };
  }, [allUsers, postProfilesByUid, resolvedPhotosByUid]);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const postsQuery = query(collection(db, "posts"), where("uid", "==", currentUser.uid));
    const unsubscribe = onSnapshot(postsQuery, (snapshot) => {
      const items = snapshot.docs
        .map((snapshotDoc) => ({ id: snapshotDoc.id, ...snapshotDoc.data() }))
        .sort((a, b) => {
          const aSeconds = a?.timestamp?.seconds || 0;
          const bSeconds = b?.timestamp?.seconds || 0;
          return bSeconds - aSeconds;
        });
      setUserPosts(items);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const savedQuery = query(
      collection(db, "posts"),
      where("savedBy", "array-contains", currentUser.uid)
    );
    const unsubscribe = onSnapshot(savedQuery, (snapshot) => {
      const items = snapshot.docs
        .map((snapshotDoc) => ({ id: snapshotDoc.id, ...snapshotDoc.data() }))
        .sort((a, b) => (b?.timestamp?.seconds || 0) - (a?.timestamp?.seconds || 0));
      setSavedPosts(items);
    });

    return () => unsubscribe();
  }, []);

  const handleOpenEdit = () => {
    setForm(profile);
    setSelectedImageFile(null);
    if (selectedImagePreview) {
      URL.revokeObjectURL(selectedImagePreview);
      setSelectedImagePreview("");
    }
    setOpenEdit(true);
  };

  const handleOpenSettings = () => {
    if (onOpenSettings) {
      onOpenSettings();
    }
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
    setOpenPostMenu(true);
  };

  const handleOpenPostActions = (event, post) => {
    event.stopPropagation();
    setSelectedPost(post);
    setOpenPostMenu(false);
    setAnchorElActions(event.currentTarget);
  };

  const handleClosePostMenu = () => {
    setOpenPostMenu(false);
    setAnchorElActions(null);
  };

  const handleLikePost = async () => {
    if (!user || !selectedPost) return;
    const postRef = doc(db, "posts", selectedPost.id);
    const liked = selectedPost.likes?.includes(user.uid);
    await updateDoc(postRef, { likes: liked ? arrayRemove(user.uid) : arrayUnion(user.uid) });
  };

  const handleSavePost = async () => {
    if (!user || !selectedPost) return;
    const postRef = doc(db, "posts", selectedPost.id);
    const saved = selectedPost.savedBy?.includes(user.uid);
    await updateDoc(postRef, { savedBy: saved ? arrayRemove(user.uid) : arrayUnion(user.uid) });
  };

  const handleSharePost = async (post) => {
    const text = post?.text || t("shareFallbackText");
    try {
      if (navigator.share) {
        await navigator.share({ title: "Gexvi", text, url: window.location.href });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setToast({ open: true, type: "success", text: t("textCopied") });
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setToast({ open: true, type: "success", text: t("linkCopied") });
      }
    } catch {}
  };

  const handleOpenEditPost = () => {
    setEditPostText(selectedPost?.text || "");
    setOpenPostMenu(false);
    setAnchorElActions(null);
    setOpenEditPost(true);
  };

  const handlePromoteNavigate = () => {
    setAnchorElActions(null);
    setOpenPostMenu(false);
    if (onNavigate) {
      onNavigate("promover");
      return;
    }
    navigate("/");
  };

  const toggleFollowUser = async (targetUid) => {
    const currentUser = auth.currentUser;
    if (!currentUser || !targetUid || targetUid === currentUser.uid || followLoadingUid) return;
    const isFollowingNow = myFollowingIds.includes(targetUid);
    const isPending = pendingFollowRequestIds.includes(targetUid);
    setFollowLoadingUid(targetUid);
    try {
      const myRef = doc(db, "users", currentUser.uid);
      const requestId = `follow_request_${currentUser.uid}_${targetUid}`;

      if (isFollowingNow) {
        // El contador de seguidores del otro usuario lo recalcula el backend.
        await updateDoc(myRef, {
          following: arrayRemove(targetUid),
          followingCount: increment(-1),
        });
        setToast({ open: true, type: "success", text: t("userUnfollowed") });
      } else if (isPending) {
        await setDoc(
          doc(db, "follow_requests", requestId),
          { status: "cancelled", updatedAt: serverTimestamp() },
          { merge: true }
        );
        setToast({ open: true, type: "success", text: t("requestCancelled") });
      } else {
        const requesterName = currentUser.displayName || currentUser.email?.split("@")[0] || t("userFallback");
        const requesterPhoto = currentUser.photoURL || "";

        await setDoc(
          doc(db, "follow_requests", requestId),
          {
            type: "follow_request",
            status: "pending",
            requesterUid: currentUser.uid,
            targetUid,
            requesterName,
            requesterPhotoURL: requesterPhoto,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        const notificationId = `follow_request_${currentUser.uid}_${Date.now()}`;
        await setDoc(
          doc(db, "users", targetUid, "notifications", notificationId),
          {
            type: "follow_request",
            actorUid: currentUser.uid,
            authorUid: currentUser.uid,
            actorPhotoURL: requesterPhoto,
            requesterPhotoURL: requesterPhoto,
            title: `${requesterName} te solicita seguir`,
            body: `${requesterName} te solicita permiso para verte.`,
            status: "pending",
            read: false,
            createdAt: serverTimestamp(),
          }
        );

        setToast({ open: true, type: "success", text: t("requestSent") });
      }
    } catch (error) {
      setToast({ open: true, type: "error", text: t("followToggleError") });
    } finally {
      setFollowLoadingUid("");
    }
  };

  const getFollowLabel = (targetUid) => {
    if (myFollowingIds.includes(targetUid)) return t("followed");
    if (pendingFollowRequestIds.includes(targetUid)) return t("pending");
    return t("follow");
  };

  const followingUsers = useMemo(() => {
    return myFollowingIds.map((followUid) => {
      const found = allUsers.find((u) => u.uid === followUid);
      if (found) return found;
      return { uid: followUid, displayName: t("userFallback"), photoURL: "", handle: "" };
    });
  }, [allUsers, myFollowingIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const followerUsers = useMemo(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];
    return allUsers.filter((u) => Array.isArray(u.following) && u.following.includes(currentUser.uid));
  }, [allUsers]);

  const suggestions = useMemo(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];
    const myFollowingSet = new Set(myFollowingIds || []);

    return allUsers
      .filter((u) => u.uid !== currentUser.uid)
      .map((u) => {
        const candidateFollowing = Array.isArray(u.following) ? u.following : [];
        let mutualCount = 0;
        candidateFollowing.forEach((id) => {
          if (myFollowingSet.has(id)) mutualCount += 1;
        });
        return { ...u, mutualCount };
      })
      .sort((a, b) => {
        if (b.mutualCount !== a.mutualCount) return b.mutualCount - a.mutualCount;
        return (b.followersCount || 0) - (a.followersCount || 0);
      })
      .slice(0, 10);
  }, [allUsers, myFollowingIds]);

  const activeConnections = connectionsTab === "followers" ? followerUsers : followingUsers;

  const followingContacts = useMemo(() => {
    return myFollowingIds
      .map((followUid) => {
        const found = allUsers.find((u) => u.uid === followUid);
        if (found) return found;
        return { uid: followUid, displayName: t("userFallback"), photoURL: "", handle: "" };
      })
      .filter((item) => item.uid && item.uid !== user?.uid);
  }, [allUsers, myFollowingIds, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveUserIdentity = (item) => {
    const postProfile = postProfilesByUid[item?.uid] || {};
    const rawPhoto = item?.photoURL || item?.photoUrl || item?.userImg || item?.avatar || postProfile.photoURL || "";
    return {
      name: item?.displayName || item?.username || postProfile.displayName || item?.handle || postProfile.handle || t("userFallback"),
      handle: item?.username || item?.handle || postProfile.username || postProfile.handle || "",
      photo: resolvedPhotosByUid[item?.uid] || rawPhoto,
    };
  };

  const handleOpenUserProfile = (targetUid) => {
    if (!targetUid || !onOpenProfile) return;
    setOpenConnectionsDialog(false);
    onOpenProfile(targetUid);
  };

  const handleRepost = async () => {
    if (!selectedPost) return;
    setAnchorElActions(null);
    const currentUser = auth.currentUser;
    try {
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : {};
      const repostMedia = Array.isArray(selectedPost.media)
        ? selectedPost.media
        : [
            ...(selectedPost.imageUrl ? [{ type: "image", url: selectedPost.imageUrl }] : []),
            ...(selectedPost.videoUrl ? [{ type: "video", url: selectedPost.videoUrl }] : []),
          ];
      const repostImageUrl =
        repostMedia.find((item) => item?.type === "image")?.url ||
        selectedPost.imageUrl ||
        "";
      const repostVideoUrl =
        repostMedia.find((item) => item?.type === "video")?.url ||
        selectedPost.videoUrl ||
        "";
      await addDoc(collection(db, "posts"), {
        uid: currentUser.uid,
        username: userData.displayName || currentUser.displayName || t("userFallback"),
        handle: userData.username || currentUser.email?.split("@")[0] || t("usernameFallback"),
        userImg: userData.photoURL || currentUser.photoURL || "",
        text: selectedPost.text || "",
        media: repostMedia,
        imageUrl: repostImageUrl,
        videoUrl: repostVideoUrl,
        likes: [],
        savedBy: [],
        commentsCount: 0,
        isRepost: true,
        originalPostId: selectedPost.id,
        timestamp: serverTimestamp(),
      });
      await updateDoc(doc(db, "posts", selectedPost.id), { repostCount: increment(1) });
      setToast({ open: true, type: "success", text: t("postReposted") });
    } catch {
      setToast({ open: true, type: "error", text: t("repostError") });
    }
  };

  const handleSendPostToContact = async (targetUid) => {
    const currentUser = auth.currentUser;
    if (!currentUser || !selectedPost || !targetUid || sendingPostUid) return;
    setSendingPostUid(targetUid);
    try {
      const targetIdentity = resolveUserIdentity({ uid: targetUid });
      const myDisplayName = profile.displayName || currentUser.displayName || currentUser.email?.split("@")[0] || "Yo";
      const myPhoto = profile.photoURL || currentUser.photoURL || "";
      const chatId = [currentUser.uid, targetUid].sort().join("_");

      await setDoc(
        doc(db, "chats", chatId),
        {
          participants: [currentUser.uid, targetUid],
          members: [currentUser.uid, targetUid],
          participantNames: {
            [currentUser.uid]: myDisplayName,
            [targetUid]: targetIdentity.name,
          },
          participantPhotos: {
            [currentUser.uid]: myPhoto,
            [targetUid]: targetIdentity.photo,
          },
          lastMessage: t("sharedPostMessage"),
          lastMessageSenderUid: currentUser.uid,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await addDoc(collection(db, "chats", chatId, "messages"), {
        uid: currentUser.uid,
        text: `${t("sharedPostMessage")}: ${selectedPost.text || t("shareFallbackText")}`,
        sharedPostId: selectedPost.id,
        sharedPostText: selectedPost.text || "",
        sharedPostImage: selectedPost.imageUrl || "",
        timestamp: serverTimestamp(),
      });

      await updateDoc(doc(db, "posts", selectedPost.id), { sentCount: increment(1) });
      setToast({ open: true, type: "success", text: t("postSentSuccess") });
      setOpenSendPostDialog(false);
    } catch {
      setToast({ open: true, type: "error", text: t("postSendError") });
    } finally {
      setSendingPostUid("");
    }
  };

  const getPrimaryMedia = (post) => {
    const mediaItems = Array.isArray(post?.media) ? post.media : [];
    const videoItem = mediaItems.find((item) => item?.type === "video") || mediaItems.find((item) => /\.(mp4|webm|ogg)(\?|$)/i.test(item?.url || ""));
    if (videoItem) return videoItem;
    const imageItem = mediaItems.find((item) => item?.type === "image") || mediaItems[0];
    if (imageItem) return imageItem;
    if (post?.videoUrl) return { type: "video", url: post.videoUrl };
    if (post?.imageUrl) return { type: "image", url: post.imageUrl };
    return null;
  };

  const postHasVideo = (post) => {
    const mediaItems = Array.isArray(post?.media) ? post.media : [];
    return Boolean(post?.videoUrl) || mediaItems.some((item) => item?.type === "video" || /\.(mp4|webm|ogg)(\?|$)/i.test(item?.url || ""));
  };

  const postHasImage = (post) => {
    const mediaItems = Array.isArray(post?.media) ? post.media : [];
    return Boolean(post?.imageUrl) || mediaItems.some((item) => item?.type === "image");
  };

  const handleSaveEditPost = async () => {
    if (!selectedPost) return;
    setSavingPost(true);
    try {
      await updateDoc(doc(db, "posts", selectedPost.id), { text: editPostText });
      setOpenEditPost(false);
      setToast({ open: true, type: "success", text: t("postEdited") });
    } catch {
      setToast({ open: true, type: "error", text: t("postEditError") });
    } finally {
      setSavingPost(false);
    }
  };

  const handleConfirmDelete = () => {
    setOpenPostMenu(false);
    setAnchorElActions(null);
    setOpenDeleteConfirm(true);
  };

  const handleDeletePost = async () => {
    if (!selectedPost) return;
    setSavingPost(true);
    try {
      await deleteDoc(doc(db, "posts", selectedPost.id));
      setOpenDeleteConfirm(false);
      setToast({ open: true, type: "success", text: t("postDeleted") });
    } catch {
      setToast({ open: true, type: "error", text: t("postDeleteError") });
    } finally {
      setSavingPost(false);
    }
  };

  useEffect(() => {
    return () => {
      if (selectedImagePreview) URL.revokeObjectURL(selectedImagePreview);
    };
  }, [selectedImagePreview]);

  const handleSelectImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setToast({ open: true, type: "error", text: t("invalidImageFile") });
      return;
    }

    if (selectedImagePreview) URL.revokeObjectURL(selectedImagePreview);
    const previewUrl = URL.createObjectURL(file);
    setSelectedImagePreview(previewUrl);
    setSelectedImageFile(file);
  };

  const handleSaveProfile = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const displayName = form.displayName.trim();
    let photoURL = form.photoURL.trim();
    const bio = form.bio.trim() || defaultBio;
    const websites = (form.websites || []).map((w) => w.trim()).filter(Boolean);
    const employment = (form.employment || "").trim();
    const position = (form.position || "").trim();
    const interests = (form.interests || "").trim();

    if (!displayName) {
      setToast({ open: true, type: "error", text: t("nameRequired") });
      return;
    }

    setSaving(true);
    try {
      if (selectedImageFile) {
        const imageRef = ref(
          storage,
          `users/${currentUser.uid}/profile-${Date.now()}-${selectedImageFile.name}`
        );
        await uploadBytes(imageRef, selectedImageFile);
        photoURL = await getDownloadURL(imageRef);
      }

      await updateProfile(currentUser, {
        displayName,
        photoURL: photoURL || null,
      });

      await setDoc(
        doc(db, "users", currentUser.uid),
        {
          displayName,
          photoURL,
          bio,
          websites,
          employment,
          position,
          interests,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const updatedProfile = {
        ...profile,
        displayName,
        photoURL,
        bio,
        websites,
        employment,
        position,
        interests,
      };
      setProfile(updatedProfile);
      setForm({ displayName, photoURL, bio, websites, employment, position, interests });
      if (selectedImagePreview) {
        URL.revokeObjectURL(selectedImagePreview);
        setSelectedImagePreview("");
      }
      setSelectedImageFile(null);
      setOpenEdit(false);
      setToast({ open: true, type: "success", text: t("profileUpdated") });
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        text: t("profileUpdateError"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        color: onSurface(1),
        minHeight: "100dvh",
        width: "100%",
        overflowX: "hidden",
        pb: { xs: 11, sm: 10 },
        background:
          isLightTheme
            ? "radial-gradient(circle at top, rgba(0,150,180,0.08) 0%, #ffffff 40%, #f2f5f8 100%)"
            : "radial-gradient(circle at top, rgba(0, 212, 255, 0.14) 0%, rgba(0,0,0,0.95) 32%, #000 100%)",
      }}
    >
      {/* Header del Perfil */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          bgcolor: isLightTheme ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.82)",
          backdropFilter: "blur(10px)",
          zIndex: 10,

        }}
      >
        <IconButton onClick={onBack || (() => navigate(-1))} sx={{ color: onSurface(1) }}>
          <ArrowBackIosNew />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
            {profile.displayName || t("myProfile")}
            {profile.verified ? <Verified sx={{ fontSize: 18, color: "#35b6ff" }} /> : null}
          </Box>
        </Typography>
        <IconButton onClick={handleOpenSettings} sx={{ color: onSurface(1) }}>
          <Settings />
        </IconButton>
      </Box>

      {/* Información de Usuario */}
      <Box sx={{ px: { xs: 2, sm: 3 }, mt: 2, mb: 3, maxWidth: "860px", mx: "auto" }}>
        <Box
          sx={{
            bgcolor: onSurface(0.035),
            border: `1px solid ${onSurface(0.08)}`,
            borderRadius: "18px",
            p: { xs: 2, sm: 2.5 },
          }}
        >
          {/* Avatar + nombre + @usuario */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={profile.photoURL}
              sx={{
                width: { xs: 60, sm: 68 },
                height: { xs: 60, sm: 68 },
                border: "2px solid #00d4ff",
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                noWrap
                sx={{ fontWeight: "bold", color: onSurface(1), display: "flex", alignItems: "center", gap: 0.6 }}
              >
                {profile.displayName || t("myProfile")}
                {profile.verified ? <Verified sx={{ fontSize: 17, color: "#35b6ff", flexShrink: 0 }} /> : null}
              </Typography>
              <Typography variant="body2" noWrap sx={{ color: onSurface(0.5) }}>
                {profile.username ? `@${profile.username}` : t("usernameFallback")}
              </Typography>
            </Box>
          </Stack>

          {/* Bio */}
          {profile.bio && (
            <Typography variant="body2" sx={{ color: onSurface(0.8), mt: 1.5 }}>
              {profile.bio}
            </Typography>
          )}

          {/* Datos extra del perfil */}
          {(profile.employment || profile.position || (profile.websites && profile.websites.length > 0) || profile.interests) && (
            <Box sx={{ mt: 1.2, display: "flex", flexDirection: "column", gap: 0.8 }}>
              {(profile.employment || profile.position) && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <WorkOutline sx={{ fontSize: 16, color: onSurface(0.45) }} />
                  <Typography variant="body2" sx={{ color: onSurface(0.75) }}>
                    {[profile.position, profile.employment].filter(Boolean).join(" en ")}
                  </Typography>
                </Stack>
              )}
              {profile.websites && profile.websites.length > 0 && (
                <Stack direction="column" spacing={0.5}>
                  {profile.websites.map((url, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                      <LinkOutlined sx={{ fontSize: 16, color: onSurface(0.45) }} />
                      <Typography
                        component="a"
                        href={url.startsWith("http") ? url : `https://${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{ color: "#00d4ff", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                      >
                        {url.replace(/^https?:\/\//, "")}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
              {profile.interests && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <StarBorderOutlined sx={{ fontSize: 16, color: onSurface(0.45) }} />
                  <Typography variant="body2" sx={{ color: onSurface(0.75) }}>
                    {profile.interests}
                  </Typography>
                </Stack>
              )}
            </Box>
          )}

          <Divider sx={{ bgcolor: onSurface(0.08), my: 1.75 }} />

          {/* Estadísticas */}
          <Stack direction="row">
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <Typography variant="subtitle1" sx={{ color: onSurface(1), fontWeight: "bold" }}>
                {userPosts.length}
              </Typography>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>
                {t("posts")}
              </Typography>
            </Box>
            <Box
              sx={{ flex: 1, textAlign: "center", cursor: "pointer" }}
              onClick={() => {
                setConnectionsTab("followers");
                setOpenConnectionsDialog(true);
              }}
            >
              <Typography variant="subtitle1" sx={{ color: onSurface(1), fontWeight: "bold" }}>
                {profile.followersCount}
              </Typography>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>
                {t("followers")}
              </Typography>
            </Box>
            <Box
              sx={{ flex: 1, textAlign: "center", cursor: "pointer" }}
              onClick={() => {
                setConnectionsTab("following");
                setOpenConnectionsDialog(true);
              }}
            >
              <Typography variant="subtitle1" sx={{ color: onSurface(1), fontWeight: "bold" }}>
                {profile.followingCount}
              </Typography>
              <Typography variant="caption" sx={{ color: onSurface(0.6) }}>
                {t("following")}
              </Typography>
            </Box>
          </Stack>

          {/* Botón de Acción */}
          <Button
            variant="contained"
            fullWidth
            startIcon={<Edit />}
            onClick={handleOpenEdit}
            sx={{
              mt: 1.75,
              bgcolor: onSurface(0.12),
              color: onSurface(1),
              textTransform: "none",
              borderRadius: "12px",
              "&:hover": { bgcolor: onSurface(0.2) },
            }}
          >
            {t("editProfile")}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ bgcolor: onSurface(0.1) }} />

      {/* Tabs de Contenido (Iconos) */}
      <Stack direction="row" justifyContent="space-around" sx={{ py: 1, maxWidth: "860px", mx: "auto" }}>
        <Box
          onClick={() => setActiveTab("imagenes")}
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            borderBottom: activeTab === "imagenes" ? "2px solid #00d4ff" : "2px solid transparent",
            py: 0.5,
            cursor: "pointer",
          }}
        >
          <GridOn sx={{ color: activeTab === "imagenes" ? "#00d4ff" : onSurface(0.5) }} />
        </Box>
        <Box
          onClick={() => setActiveTab("videos")}
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            borderBottom: activeTab === "videos" ? "2px solid #00d4ff" : "2px solid transparent",
            py: 0.5,
            cursor: "pointer",
          }}
        >
          <VideoLibrary sx={{ color: activeTab === "videos" ? "#00d4ff" : onSurface(0.5) }} />
        </Box>
        <Box
          onClick={() => setActiveTab("guardados")}
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            borderBottom: activeTab === "guardados" ? "2px solid #00d4ff" : "2px solid transparent",
            py: 0.5,
            cursor: "pointer",
          }}
        >
          <BookmarkBorder sx={{ color: activeTab === "guardados" ? "#00d4ff" : onSurface(0.5) }} />
        </Box>
      </Stack>

      {/* Grid de publicaciones del usuario */}
      {(() => {
        const imagesTabPosts = userPosts.filter((post) => postHasImage(post) || (!postHasVideo(post) && !postHasImage(post)));
        const videosTabPosts = userPosts.filter((post) => postHasVideo(post));
        const visiblePosts = activeTab === "guardados" ? savedPosts : activeTab === "videos" ? videosTabPosts : imagesTabPosts;
        return (
          <>
      <Grid container spacing={0.75} sx={{ px: { xs: 1, sm: 2 }, maxWidth: "860px", mx: "auto" }}>
        {visiblePosts.map((post) => (
          <Grid size={4} key={post.id}>
            <Box
              onClick={() => activeTab !== "guardados" && handlePostClick(post)}
              sx={{
                aspectRatio: "1/1",
                bgcolor: onSurface(0.04),
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                position: "relative",
                cursor: activeTab !== "guardados" ? "pointer" : "default",
                "&:hover": activeTab !== "guardados" ? { opacity: 0.8, transform: "scale(0.98)", transition: "all 0.15s" } : {},
              }}
            >
              {activeTab !== "guardados" && Boolean(user?.uid) && (
                <IconButton
                  onClick={(event) => handleOpenPostActions(event, post)}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    zIndex: 2,
                    p: 0.45,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                  }}
                >
                  <MoreVert sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              {getPrimaryMedia(post) ? (() => {
                const primaryMedia = getPrimaryMedia(post);
                const isVideo = primaryMedia?.type === "video" || /\.(mp4|webm|ogg)(\?|$)/i.test(primaryMedia?.url || "");
                return (
                  <Box
                    component={isVideo ? "video" : "img"}
                    src={primaryMedia?.url || ""}
                    controls={isVideo}
                    muted={isVideo}
                    playsInline={isVideo}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                );
              })() : (
                <Typography
                  variant="caption"
                  sx={{
                    color: onSurface(0.62),
                    px: 1,
                    textAlign: "center",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {post.text || t("noTextFallback")}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      {visiblePosts.length === 0 && (
        <Typography sx={{ color: onSurface(0.6), textAlign: "center", mt: 3 }}>
          {activeTab === "guardados" ? t("noSavedPosts") : activeTab === "videos" ? t("noVideoPosts") : t("noPosts")}
        </Typography>
      )}
          </>
        );
      })()}

      <Dialog
        open={openEdit}
        onClose={() => !saving && setOpenEdit(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("editProfileTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={selectedImagePreview || form.photoURL || ""}
                sx={{ width: 64, height: 64, border: "2px solid rgba(0,212,255,0.7)" }}
              />
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving}
                  sx={{
                    textTransform: "none",
                    color: onSurface(1),
                    borderColor: onSurface(0.35),
                    "&:hover": { borderColor: "#00d4ff", bgcolor: "rgba(0,212,255,0.08)" },
                  }}
                >
                  {t("uploadImage")}
                </Button>
                <Typography variant="caption" sx={{ display: "block", mt: 1, color: onSurface(0.7) }}>
                  {selectedImageFile ? selectedImageFile.name : t("noFileSelected")}
                </Typography>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleSelectImage}
                style={{ display: "none" }}
              />
            </Stack>
            <TextField
              label={t("nameLabel")}
              value={form.displayName}
              onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
              fullWidth
              disabled={saving}
              sx={getEditInputSx(onSurface)}
            />
            <TextField
              label={t("bioLabel")}
              value={form.bio}
              onChange={(e) => setForm((prev) => ({ ...prev, bio: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
              disabled={saving}
              sx={getEditInputSx(onSurface)}
            />
            {/* Sitios web (múltiples) */}
            <Box>
              <Typography variant="caption" sx={{ color: onSurface(0.55), mb: 0.5, display: "block" }}>
                {t("websitesLabel")}
              </Typography>
              <Stack spacing={1}>
                {(form.websites || []).map((url, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <TextField
                      value={url}
                      onChange={(e) => {
                        const updated = [...form.websites];
                        updated[idx] = e.target.value;
                        setForm((prev) => ({ ...prev, websites: updated }));
                      }}
                      fullWidth
                      disabled={saving}
                      placeholder="https://tusitio.com"
                      sx={getEditInputSx(onSurface)}
                    />
                    <IconButton
                      size="small"
                      disabled={saving}
                      onClick={() => setForm((prev) => ({ ...prev, websites: prev.websites.filter((_, i) => i !== idx) }))}
                      sx={{ color: "#ff4d4d", flexShrink: 0 }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  size="small"
                  startIcon={<AddCircleOutline />}
                  disabled={saving}
                  onClick={() => setForm((prev) => ({ ...prev, websites: [...(prev.websites || []), ""] }))}
                  sx={{ alignSelf: "flex-start", textTransform: "none", color: "#00d4ff" }}
                >
                  {t("addWebsite")}
                </Button>
              </Stack>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={t("employmentLabel")}
                value={form.employment}
                onChange={(e) => setForm((prev) => ({ ...prev, employment: e.target.value }))}
                fullWidth
                disabled={saving}
                sx={getEditInputSx(onSurface)}
              />
              <TextField
                label={t("positionLabel")}
                value={form.position}
                onChange={(e) => setForm((prev) => ({ ...prev, position: e.target.value }))}
                fullWidth
                disabled={saving}
                sx={getEditInputSx(onSurface)}
              />
            </Stack>
            <TextField
              label={t("interestsLabel")}
              value={form.interests}
              onChange={(e) => setForm((prev) => ({ ...prev, interests: e.target.value }))}
              fullWidth
              disabled={saving}
              placeholder={t("interestsPlaceholder")}
              sx={getEditInputSx(onSurface)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenEdit(false)}
            disabled={saving}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSaveProfile}
            disabled={saving}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 120,
              background: "linear-gradient(90deg, #00d4ff 0%, #1498ff 100%)",
            }}
          >
            {saving ? <CircularProgress size={20} sx={{ color: "#00131a" }} /> : t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={2600}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.type}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {toast.text}
        </Alert>
      </Snackbar>

      <Dialog
        open={openConnectionsDialog}
        onClose={() => setOpenConnectionsDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
            overflow: "hidden",
            maxHeight: "84vh",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1, borderBottom: `1px solid ${onSurface(0.08)}` }}>
          <Stack direction="row" spacing={0.5}>
            <Button
              onClick={() => setConnectionsTab("followers")}
              sx={{
                textTransform: "none",
                minWidth: 100,
                color: connectionsTab === "followers" ? "#00d4ff" : onSurface(0.7),
                borderBottom: connectionsTab === "followers" ? "2px solid #00d4ff" : "2px solid transparent",
                borderRadius: 0,
              }}
            >
              {t("followers")}
            </Button>
            <Button
              onClick={() => setConnectionsTab("following")}
              sx={{
                textTransform: "none",
                minWidth: 100,
                color: connectionsTab === "following" ? "#00d4ff" : onSurface(0.7),
                borderBottom: connectionsTab === "following" ? "2px solid #00d4ff" : "2px solid transparent",
                borderRadius: 0,
              }}
            >
              {t("following")}
            </Button>
          </Stack>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setOpenConnectionsDialog(false)} sx={{ color: onSurface(0.7) }}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, py: 1.5, overflowY: "auto" }}>
          <Typography variant="caption" sx={{ color: onSurface(0.5), letterSpacing: 0.3 }}>
            {connectionsTab === "followers" ? t("peopleWhoFollowYou") : t("peopleYouFollow")}
          </Typography>

          <Stack spacing={1} sx={{ mt: 1.2 }}>
            {activeConnections.length === 0 && (
              <Typography sx={{ color: onSurface(0.55), py: 1.5 }}>
                {t("noUsersToShow")}
              </Typography>
            )}

            {activeConnections.map((item) => {
              const isMe = user?.uid === item.uid;
              const iFollow = myFollowingIds.includes(item.uid);
              const identity = resolveUserIdentity(item);
              return (
                <Box
                  key={`${connectionsTab}-${item.uid}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    p: 1,
                    borderRadius: "12px",
                    bgcolor: onSurface(0.03),
                    border: `1px solid ${onSurface(0.07)}`,
                  }}
                >
                  <Avatar
                    src={identity.photo}
                    sx={{ width: 40, height: 40, cursor: "pointer" }}
                    onClick={() => handleOpenUserProfile(item.uid)}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      noWrap
                      sx={{ fontWeight: 700, cursor: "pointer" }}
                      onClick={() => handleOpenUserProfile(item.uid)}
                    >
                      {identity.name}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ color: onSurface(0.55), display: "block" }}>
                      @{identity.handle || t("usernameFallback")}
                    </Typography>
                  </Box>
                  {!isMe && (
                    <Button
                      onClick={() => toggleFollowUser(item.uid)}
                      disabled={followLoadingUid === item.uid}
                      sx={{
                        textTransform: "none",
                        minWidth: 90,
                        borderRadius: "10px",
                        fontWeight: 600,
                        bgcolor: iFollow ? onSurface(0.1) : "#00d4ff",
                        color: iFollow ? onSurface(1) : "#001217",
                        "&:hover": {
                          bgcolor: iFollow ? onSurface(0.18) : "#00b8d9",
                        },
                      }}
                    >
                      {followLoadingUid === item.uid ? <CircularProgress size={16} /> : iFollow ? t("followed") : getFollowLabel(item.uid)}
                    </Button>
                  )}
                </Box>
              );
            })}
          </Stack>

          <Divider sx={{ bgcolor: onSurface(0.08), my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t("suggestions")}
          </Typography>
          <Typography variant="caption" sx={{ color: onSurface(0.5), display: "block", mb: 1.2 }}>
            {t("suggestionsDesc")}
          </Typography>

          <Stack spacing={1}>
            {suggestions.length === 0 && (
              <Typography sx={{ color: onSurface(0.55), py: 1.5 }}>
                {t("noSuggestions")}
              </Typography>
            )}

            {suggestions.map((item) => {
              const iFollow = myFollowingIds.includes(item.uid);
              const identity = resolveUserIdentity(item);
              return (
                <Box
                  key={`suggestion-${item.uid}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    p: 1,
                    borderRadius: "12px",
                    bgcolor: onSurface(0.03),
                    border: `1px solid ${onSurface(0.07)}`,
                  }}
                >
                  <Avatar
                    src={identity.photo}
                    sx={{ width: 40, height: 40, cursor: "pointer" }}
                    onClick={() => handleOpenUserProfile(item.uid)}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      noWrap
                      sx={{ fontWeight: 700, cursor: "pointer" }}
                      onClick={() => handleOpenUserProfile(item.uid)}
                    >
                      {identity.name}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ color: onSurface(0.55), display: "block" }}>
                      {item.mutualCount} {t("mutualFollowers")}
                    </Typography>
                  </Box>
                  <Button
                    onClick={() => toggleFollowUser(item.uid)}
                    disabled={followLoadingUid === item.uid}
                    sx={{
                      textTransform: "none",
                      minWidth: 90,
                      borderRadius: "10px",
                      fontWeight: 600,
                      bgcolor: iFollow ? onSurface(0.1) : "#00d4ff",
                      color: iFollow ? onSurface(1) : "#001217",
                      "&:hover": {
                        bgcolor: iFollow ? onSurface(0.18) : "#00b8d9",
                      },
                    }}
                  >
                    {followLoadingUid === item.uid ? <CircularProgress size={16} /> : iFollow ? t("followed") : getFollowLabel(item.uid)}
                  </Button>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Dialog>

      {/* Post dialog — vista completa */}
      {(() => {
        const livePost = userPosts.find((p) => p.id === selectedPost?.id) || selectedPost;
        const hasLiked = livePost?.likes?.includes(user?.uid);
        const hasSaved = livePost?.savedBy?.includes(user?.uid);
        return (
          <Dialog
            open={openPostMenu}
            onClose={handleClosePostMenu}
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                bgcolor: dialogBg,
                color: onSurface(1),
                borderRadius: "16px",
                backgroundImage: dialogGradient,
                overflow: "hidden",
                width: { xs: "98vw", sm: "94vw", md: "980px" },
                maxWidth: "1100px",
              },
            }}
          >
            {livePost && (
              <>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1.5, borderBottom: `1px solid ${onSurface(0.08)}` }}>
                  <Avatar src={livePost.userImg || profile.photoURL} sx={{ width: 38, height: 38, mr: 1.5 }} />
                  <Box flex={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        {livePost.username || profile.displayName}
                        {(profile.verified || livePost.userVerified) ? <Verified sx={{ fontSize: 15, color: "#35b6ff" }} /> : null}
                      </Box>
                    </Typography>
                    {livePost.handle && (
                      <Typography variant="caption" sx={{ color: onSurface(0.45) }}>
                        @{livePost.handle}
                      </Typography>
                    )}
                  </Box>
                  <IconButton onClick={(e) => setAnchorElActions(e.currentTarget)} sx={{ color: onSurface(0.7) }}>
                    <MoreVert />
                  </IconButton>
                  <IconButton onClick={handleClosePostMenu} sx={{ color: onSurface(0.7) }}>
                    <Close />
                  </IconButton>
                </Box>

                {/* Media */}
                {getPrimaryMedia(livePost) && (() => {
                  const primaryMedia = getPrimaryMedia(livePost);
                  const isVideo = primaryMedia?.type === "video" || /\.(mp4|webm|ogg)(\?|$)/i.test(primaryMedia?.url || "");
                  return (
                    <Box
                      component={isVideo ? "video" : "img"}
                      src={primaryMedia?.url || ""}
                      controls={Boolean(isVideo)}
                      muted={Boolean(isVideo)}
                      playsInline={Boolean(isVideo)}
                      sx={{
                        width: "100%",
                        display: "block",
                        objectFit: "cover",
                        bgcolor: "#000",
                        height: "auto",
                        minHeight: "52vh",
                        maxHeight: "86vh",
                      }}
                    />
                  );
                })()}

                {/* Texto */}
                <Box sx={{ px: 2, py: 1.5 }}>
                  <PostTextBlock text={livePost.text} textBackground={livePost.textBackground} isLightTheme={isLightTheme} />
                </Box>

                {/* Acciones */}
                <Divider sx={{ bgcolor: onSurface(0.08) }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, py: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.2}>
                    <IconButton onClick={handleLikePost} sx={{ color: hasLiked ? "#ff4b4b" : onSurface(0.8), p: 0.45 }}>
                      {hasLiked ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                    </IconButton>
                    <Typography variant="caption" sx={{ mr: 0.35, fontSize: "0.68rem" }}>{livePost.likes?.length || 0}</Typography>
                    <IconButton onClick={() => setOpenComentariosPost(true)} sx={{ color: onSurface(0.8), p: 0.45 }}>
                      <ChatBubbleOutline fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ mr: 0.35, fontSize: "0.68rem" }}>{livePost.commentsCount || 0}</Typography>
                    <IconButton onClick={handleRepost} sx={{ color: onSurface(0.8), p: 0.45 }}>
                      <Repeat fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ color: onSurface(0.8), mr: 0.35, fontSize: "0.68rem" }}>
                      {livePost.repostCount || 0}
                    </Typography>
                    <IconButton onClick={() => setOpenSendPostDialog(true)} sx={{ color: onSurface(0.8), p: 0.45 }}>
                      <Send fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ color: onSurface(0.8), fontSize: "0.68rem" }}>
                      {livePost.sentCount || 0}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.2}>
                    <IconButton onClick={handleSavePost} sx={{ color: hasSaved ? "#00d4ff" : onSurface(0.8), p: 0.45 }}>
                      {hasSaved ? <Bookmark fontSize="small" /> : <BookmarkBorder fontSize="small" />}
                    </IconButton>
                    <IconButton onClick={() => handleSharePost(livePost)} sx={{ color: onSurface(0.8), p: 0.45 }}>
                      <IosShare fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </>
            )}
          </Dialog>
        );
      })()}

      <Dialog
        open={openSendPostDialog}
        onClose={() => !sendingPostUid && setOpenSendPostDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("sendToContacts")}</DialogTitle>
        <DialogContent>
          {followingContacts.length === 0 ? (
            <Typography sx={{ color: onSurface(0.65), mt: 1 }}>
              {t("noContacts")}
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {followingContacts.map((contact) => {
                const identity = resolveUserIdentity(contact);
                return (
                  <Box
                    key={contact.uid}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      p: 1,
                      borderRadius: "12px",
                      bgcolor: onSurface(0.03),
                      border: `1px solid ${onSurface(0.07)}`,
                    }}
                  >
                    <Avatar src={identity.photo} sx={{ width: 38, height: 38 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                        {identity.name}
                      </Typography>
                      <Typography variant="caption" noWrap sx={{ color: onSurface(0.55), display: "block" }}>
                        @{identity.handle || t("usernameFallback")}
                      </Typography>
                    </Box>
                    <Button
                      onClick={() => handleSendPostToContact(contact.uid)}
                      disabled={sendingPostUid === contact.uid}
                      sx={{
                        textTransform: "none",
                        minWidth: 88,
                        borderRadius: "10px",
                        fontWeight: 600,
                        bgcolor: "#00d4ff",
                        color: "#001217",
                        "&:hover": { bgcolor: "#00b8d9" },
                      }}
                    >
                      {sendingPostUid === contact.uid ? <CircularProgress size={16} /> : t("send")}
                    </Button>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenSendPostDialog(false)}
            disabled={Boolean(sendingPostUid)}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Menú tres puntos del post */}
      <Menu
        anchorEl={anchorElActions}
        open={Boolean(anchorElActions)}
        onClose={() => setAnchorElActions(null)}
        PaperProps={{ sx: { bgcolor: dialogBg, color: onSurface(1), borderRadius: "12px", minWidth: 180 } }}
      >
        <MenuItem onClick={handleOpenEditPost} sx={{ gap: 1.5, py: 1 }}>
          <Edit fontSize="small" /> {t("menuEdit")}
        </MenuItem>
        <Divider sx={{ bgcolor: onSurface(0.1), my: 0.5 }} />
        <MenuItem onClick={handleConfirmDelete} sx={{ gap: 1.5, py: 1, color: "#ff4d4d" }}>
          <DeleteOutline fontSize="small" sx={{ color: "#ff4d4d" }} /> {t("menuDelete")}
        </MenuItem>
        <Divider sx={{ bgcolor: onSurface(0.1), my: 0.5 }} />
        <MenuItem
          onClick={handlePromoteNavigate}
          sx={{ gap: 1.5, py: 1, color: "#ffd700" }}
        >
          <Campaign fontSize="small" sx={{ color: "#ffd700" }} /> {t("menuPromote")}
        </MenuItem>
      </Menu>

      {/* Comentarios del post */}
      <Comentarios
        open={openComentariosPost}
        handleClose={() => setOpenComentariosPost(false)}
        postId={selectedPost?.id}
        onOpenProfile={onOpenProfile}
        language={language}
        themeMode={themeMode}
        zIndex={1800}
      />

      {/* Diálogo — editar post */}
      <Dialog
        open={openEditPost}
        onClose={() => !savingPost && setOpenEditPost(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("editPostTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            value={editPostText}
            onChange={(e) => setEditPostText(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            disabled={savingPost}
            placeholder={t("editPostPlaceholder")}
            sx={{ mt: 1, ...getEditInputSx(onSurface) }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenEditPost(false)}
            disabled={savingPost}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSaveEditPost}
            disabled={savingPost}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 110,
              background: "linear-gradient(90deg, #00d4ff 0%, #1498ff 100%)",
            }}
          >
            {savingPost ? <CircularProgress size={20} sx={{ color: "#00131a" }} /> : t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo — confirmar eliminación */}
      <Dialog
        open={openDeleteConfirm}
        onClose={() => !savingPost && setOpenDeleteConfirm(false)}
        PaperProps={{ sx: { bgcolor: dialogBg, color: onSurface(1), borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("deletePostTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: onSurface(0.7) }}>
            {t("deletePostWarning")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenDeleteConfirm(false)}
            disabled={savingPost}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleDeletePost}
            disabled={savingPost}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 110,
              bgcolor: "#ff4d4d",
              "&:hover": { bgcolor: "#e53935" },
            }}
          >
            {savingPost ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : t("menuDelete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const getEditInputSx = (onSurface) => ({
  "& .MuiInputBase-root": {
    color: onSurface(1),
    bgcolor: onSurface(0.04),
    borderRadius: "10px",
  },
  "& .MuiInputLabel-root": {
    color: onSurface(0.62),
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: onSurface(0.16),
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: onSurface(0.3),
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#00d4ff",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#00d4ff",
  },
});

export default Perfil;